import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Plus, Edit2, UserX, Shield, User, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function UserModal({ user, onClose, onSuccess }) {
  const [form, setForm] = useState(user || { name: '', mobile: '', email: '', role: 'staff', password: '', permissions: { canCollectPayment: true, canManageCustomers: true, canViewReports: false, canManageDevices: false, canManageComplaints: true, canManagePlans: false } });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (user?._id) await userAPI.update(user._id, form);
      else await userAPI.create(form);
      toast.success(user ? 'User updated' : 'User created');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const togglePerm = (key) => setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{user ? 'Edit User' : 'Add Staff User'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile *</label>
                <input className="form-control" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} required />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">{user ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" className="form-control" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!user} minLength={6} placeholder="Min 6 characters" />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {form.role === 'staff' && (
              <div>
                <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                  Staff Permissions
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {Object.entries(form.permissions || {}).map(([key, val]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={val} onChange={() => togglePerm(key)} />
                      {key.replace(/([A-Z])/g, ' $1').replace('can ', 'Can ').trim()}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  if (role === 'master') return (
    <span className="badge" style={{ background: '#f59e0b', color: '#fff', textTransform: 'uppercase', fontWeight: 700 }}>
      <Star size={10} style={{ display: 'inline', marginRight: 3 }} />Master
    </span>
  );
  if (role === 'admin') return (
    <span className="badge badge-danger" style={{ textTransform: 'capitalize' }}>
      <Shield size={10} style={{ display: 'inline', marginRight: 3 }} />Admin
    </span>
  );
  return (
    <span className="badge badge-info" style={{ textTransform: 'capitalize' }}>
      <User size={10} style={{ display: 'inline', marginRight: 3 }} />Staff
    </span>
  );
}

export default function UsersPage() {
  const { isMaster } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await userAPI.getAll();
      setUsers(data.data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await userAPI.delete(id);
      toast.success('User deactivated');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const companies = isMaster
    ? ['all', ...new Set(users.map(u => u.companyLabel).filter(Boolean))]
    : [];

  const visible = isMaster && filterCompany !== 'all'
    ? users.filter(u => u.companyLabel === filterCompany)
    : users;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>
            {isMaster ? 'All Users — Master View' : 'Staff Users'}
          </h2>
          <p className="text-muted text-small">
            {isMaster ? `${users.length} total users across all companies` : 'Manage admin and staff accounts'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isMaster && companies.length > 2 && (
            <select
              className="form-control"
              style={{ width: 'auto', fontSize: '.8rem', padding: '4px 10px' }}
              value={filterCompany}
              onChange={e => setFilterCompany(e.target.value)}
            >
              {companies.map(c => <option key={c} value={c}>{c === 'all' ? 'All Companies' : c}</option>)}
            </select>
          )}
          <button className="btn btn-primary" onClick={() => setModal({})}>
            <Plus size={15} /> Add Staff
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  {isMaster && <th>Company</th>}
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(u => (
                  <tr key={u._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar-circle" style={{ width: 32, height: 32, fontSize: '.75rem' }}>{u.name[0]}</div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{u.name}</div>
                          <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{u.email || u.mobile}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '.85rem' }}>{u.mobile}</td>
                    <td><RoleBadge role={u.role} /></td>
                    {isMaster && (
                      <td style={{ fontSize: '.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                        {u.companyLabel || '—'}
                      </td>
                    )}
                    <td><span className={`badge badge-${u.isActive ? 'success' : 'warning'}`}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
                      {u.lastLogin ? format(new Date(u.lastLogin), 'dd/MM/yy HH:mm') : 'Never'}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={() => setModal(u)}><Edit2 size={12} /></button>
                        {u.isActive && u.role !== 'master' && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeactivate(u._id)}><UserX size={12} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!visible.length && (
                  <tr><td colSpan={isMaster ? 7 : 6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>No users found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal !== null && (
        <UserModal
          user={modal?._id ? modal : null}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
